import { format } from 'date-fns'

import type { AccountTypes } from '@/modules/projects'
import {
  BLOCK_HEIGHT,
  COVERAGE_FILL_HEX,
  LABEL_WIDTH,
  MARGIN,
  MONTH_WIDTH,
  ROW_HEIGHT,
} from './constants'
import type { AccountCoverage, StatementCoverage } from './types'

interface ExportOptions {
  projectName?: string
  slug?: string
  includeReconciliation?: boolean
}

const SCALE = 2
const FONT = '"Geist Sans", sans-serif'

const ACCOUNT_TYPE_COLORS: Record<
  string,
  { text: string; border: string; bg: string }
> = {
  // Mirrors the AccountTypeBadge tailwind palette (700/200/50 per family).
  'Credit card': { text: '#b45309', border: '#fde68a', bg: '#fffbeb' },
  Checking: { text: '#1d4ed8', border: '#bfdbfe', bg: '#eff6ff' },
  Savings: { text: '#7e22ce', border: '#e9d5ff', bg: '#faf5ff' },
  'Debit card': { text: '#047857', border: '#a7f3d0', bg: '#ecfdf5' },
  Loan: { text: '#c2410c', border: '#fed7aa', bg: '#fff7ed' },
  Investment: { text: '#0f766e', border: '#99f6e4', bg: '#f0fdfa' },
  Retirement: { text: '#4338ca', border: '#c7d2fe', bg: '#eef2ff' },
  Other: { text: '#334155', border: '#e2e8f0', bg: '#f8fafc' },
}

function s(value: number) {
  return value * SCALE
}

function drawBadge(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  align: 'left' | 'right',
  type?: AccountTypes,
) {
  const colors = ACCOUNT_TYPE_COLORS[type ?? ''] ?? ACCOUNT_TYPE_COLORS.Other
  const fontSize = s(9)
  ctx.font = `500 ${fontSize}px ${FONT}`
  const textWidth = ctx.measureText(text).width
  const paddingX = s(5)
  const paddingY = s(3)
  const height = fontSize + paddingY * 2
  const width = textWidth + paddingX * 2
  const badgeX = align === 'right' ? x - width : x
  const r = height / 2

  // Background
  ctx.beginPath()
  ctx.roundRect(badgeX, y, width, height, r)
  ctx.fillStyle = colors.bg
  ctx.fill()
  ctx.strokeStyle = colors.border
  ctx.lineWidth = s(1)
  ctx.stroke()

  // Text
  ctx.fillStyle = colors.text
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, badgeX + paddingX, y + height / 2)

  return width
}

function computeTotalMonths(coverage: StatementCoverage): number {
  const startMonth =
    coverage.globalMin.getFullYear() * 12 + coverage.globalMin.getMonth()
  return coverage.accounts.reduce((max, acc) => {
    const accOffset =
      acc.minDate.getFullYear() * 12 + acc.minDate.getMonth() - startMonth
    return Math.max(max, accOffset + acc.monthlyStatus.length)
  }, 0)
}

function drawAccountLabels(
  ctx: CanvasRenderingContext2D,
  accounts: AccountCoverage[],
  offsetY: number,
) {
  accounts.forEach((account, i) => {
    const centerY = offsetY + s(MARGIN.top) + s(i * ROW_HEIGHT + ROW_HEIGHT / 2)

    // Account number; a member card reads muted with a branch mark so the hierarchy survives export.
    ctx.fillStyle = account.parentId ? '#71717a' : '#09090b'
    ctx.font = `500 ${s(12)}px ${FONT}`
    ctx.textAlign = 'right'
    ctx.textBaseline = 'bottom'
    ctx.fillText(
      account.parentId ? `└ ${account.id}` : account.id,
      s(LABEL_WIDTH - 12),
      centerY - s(2),
    )

    if (account.parentId) {
      ctx.fillStyle = '#71717a'
      ctx.font = `400 ${s(9)}px ${FONT}`
      ctx.textBaseline = 'top'
      ctx.fillText(
        `member of ${account.parentId}`,
        s(LABEL_WIDTH - 12),
        centerY + s(2),
      )
    } else if (account.type) {
      drawBadge(
        ctx,
        account.type,
        s(LABEL_WIDTH - 12),
        centerY + s(1),
        'right',
        account.type,
      )
    }
  })
}

function drawChart(
  ctx: CanvasRenderingContext2D,
  coverage: StatementCoverage,
  offsetY: number,
) {
  const { accounts, globalMin } = coverage
  const startMonth = globalMin.getFullYear() * 12 + globalMin.getMonth()
  const totalMonths = computeTotalMonths(coverage)

  // Grid lines, year labels, and month labels
  for (let m = 0; m <= totalMonths; m++) {
    const date = new Date(globalMin.getFullYear(), globalMin.getMonth() + m, 1)
    const xPos = s(MARGIN.left + m * MONTH_WIDTH)
    const isYear = date.getMonth() === 0
    const isQuarter = date.getMonth() % 3 === 0

    // Grid lines only at quarter boundaries
    if (isQuarter) {
      ctx.beginPath()
      ctx.moveTo(xPos, offsetY + s(MARGIN.top - 10))
      ctx.lineTo(xPos, offsetY + s(MARGIN.top + accounts.length * ROW_HEIGHT))
      ctx.strokeStyle = isYear
        ? 'rgba(156, 163, 175, 0.5)'
        : 'rgba(156, 163, 175, 0.2)'
      ctx.lineWidth = s(1)
      if (!isYear) ctx.setLineDash([s(4), s(4)])
      else ctx.setLineDash([])
      ctx.stroke()
      ctx.setLineDash([])
    }

    // Year label
    if (isYear) {
      ctx.fillStyle = '#71717a'
      ctx.font = `500 ${s(10)}px ${FONT}`
      ctx.textAlign = 'start'
      ctx.textBaseline = 'alphabetic'
      ctx.fillText(format(date, 'yyyy'), xPos, offsetY + s(MARGIN.top - 16))
    }

    // Month abbreviation below the chart
    const monthLabel = format(date, 'MMM')
    ctx.fillStyle = '#a1a1aa'
    ctx.font = `400 ${s(6)}px ${FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    ctx.fillText(
      monthLabel,
      xPos + s(MONTH_WIDTH / 2),
      offsetY + s(MARGIN.top + accounts.length * ROW_HEIGHT + 4),
    )
  }

  // Coverage blocks
  accounts.forEach((account, i) => {
    const yPos = i * ROW_HEIGHT + (ROW_HEIGHT - BLOCK_HEIGHT) / 2
    const accStartOffset =
      account.minDate.getFullYear() * 12 +
      account.minDate.getMonth() -
      startMonth

    account.monthlyStatus.forEach((status, mi) => {
      const monthOffset = accStartOffset + mi
      const xStart = monthOffset * MONTH_WIDTH
      const blockW = 16
      const xOffset = 2
      const hasNoStatement =
        status.status === 'missing' || status.status === 'verified_gap'

      const x = s(MARGIN.left + xStart + xOffset + (hasNoStatement ? 0.5 : 0))
      const y = offsetY + s(MARGIN.top + yPos + (hasNoStatement ? 0.5 : 0))
      const w = s(blockW - (hasNoStatement ? 1 : 0))
      const h = s(BLOCK_HEIGHT - (hasNoStatement ? 1 : 0))
      const r = s(2)

      ctx.beginPath()
      ctx.roundRect(x, y, w, h, r)

      ctx.fillStyle = COVERAGE_FILL_HEX[status.status]
      ctx.fill()
      if (hasNoStatement) {
        ctx.strokeStyle =
          status.status === 'verified_gap' ? '#34d399' : '#a3a3a3'
        ctx.lineWidth = s(1)
        ctx.stroke()
      }
    })
  })
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(', ')
  const lines: string[] = []
  let current = ''

  for (const word of words) {
    const candidate = current ? `${current}, ${word}` : word
    if (ctx.measureText(candidate).width > maxWidth && current) {
      lines.push(current)
      current = word
    } else {
      current = candidate
    }
  }
  if (current) lines.push(current)
  return lines
}

function measureSummaryRowHeight(
  ctx: CanvasRenderingContext2D,
  account: AccountCoverage,
  maxTextWidth: number,
): number {
  const baseHeight = s(44)
  if (account.parentId) return baseHeight
  if (account.missingText === 'Complete') return baseHeight

  ctx.font = `400 ${s(9)}px ${FONT}`
  const lines = wrapText(ctx, account.missingText, maxTextWidth)
  const lineHeight = s(13)
  const textHeight = lines.length * lineHeight
  return Math.max(baseHeight, s(36) + textHeight + s(8))
}

function drawAccountSummary(
  ctx: CanvasRenderingContext2D,
  accounts: AccountCoverage[],
  canvasWidth: number,
  offsetY: number,
): number {
  const px = s(24)
  const maxTextWidth = canvasWidth * 0.55

  // Measure row heights
  const rowHeights = accounts.map((account) =>
    measureSummaryRowHeight(ctx, account, maxTextWidth),
  )

  let runningY = offsetY

  accounts.forEach((account, i) => {
    const y = runningY
    const rowH = rowHeights[i]

    // Divider line
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(canvasWidth, y)
    ctx.strokeStyle = '#e5e7eb'
    ctx.lineWidth = s(1)
    ctx.setLineDash([])
    ctx.stroke()

    // Left: account id, indented with a branch mark when it's a member card
    const idText = account.parentId ? `└ ${account.id}` : account.id
    const idX = account.parentId ? px + s(12) : px
    ctx.fillStyle = account.parentId ? '#71717a' : '#09090b'
    ctx.font = `500 ${s(13)}px ${FONT}`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    ctx.fillText(idText, idX, y + s(10))

    // Role badge next to id
    const idWidth = ctx.measureText(idText).width
    if (account.parentId) {
      drawBadge(ctx, 'Member card', idX + idWidth + s(8), y + s(8), 'left')
    } else if (account.type) {
      drawBadge(
        ctx,
        account.type,
        px + idWidth + s(8),
        y + s(8),
        'left',
        account.type,
      )
    }

    // Range text
    ctx.fillStyle = '#71717a'
    ctx.font = `400 ${s(11)}px ${FONT}`
    ctx.textAlign = 'left'
    ctx.fillText(account.rangeText, idX, y + s(30))

    // Right: coverage status
    ctx.textAlign = 'right'
    const rightX = canvasWidth - px

    if (account.parentId) {
      const label = `Reconciled under ${account.parentId}`
      ctx.font = `500 ${s(12)}px ${FONT}`
      const labelW = ctx.measureText(label).width

      // Violet dot matching the member_activity swatch
      ctx.beginPath()
      ctx.arc(rightX - labelW - s(10), y + s(20), s(3), 0, Math.PI * 2)
      ctx.fillStyle = '#a78bfa'
      ctx.fill()

      ctx.fillStyle = '#6d28d9'
      ctx.fillText(label, rightX, y + s(24))
    } else if (account.missingText === 'Complete') {
      ctx.font = `500 ${s(12)}px ${FONT}`
      const labelW = ctx.measureText('Complete Coverage').width

      // Green dot
      ctx.beginPath()
      ctx.arc(rightX - labelW - s(10), y + s(20), s(3), 0, Math.PI * 2)
      ctx.fillStyle = '#10b981'
      ctx.fill()

      ctx.fillStyle = '#059669'
      ctx.fillText('Complete Coverage', rightX, y + s(24))
    } else {
      const label = 'Potentially Missing'
      ctx.font = `500 ${s(12)}px ${FONT}`
      const labelW = ctx.measureText(label).width

      // Red dot
      ctx.beginPath()
      ctx.arc(rightX - labelW - s(10), y + s(14), s(3), 0, Math.PI * 2)
      ctx.fillStyle = '#ef4444'
      ctx.fill()

      ctx.fillStyle = '#dc2626'
      ctx.fillText(label, rightX, y + s(18))

      // Missing months text — wrapped
      ctx.fillStyle = '#71717a'
      ctx.font = `400 ${s(9)}px ${FONT}`
      const lines = wrapText(ctx, account.missingText, maxTextWidth)
      const lineHeight = s(13)
      lines.forEach((line, li) => {
        ctx.fillText(line, rightX, y + s(34) + li * lineHeight)
      })
    }

    runningY += rowH
  })

  // Bottom divider
  ctx.beginPath()
  ctx.moveTo(0, runningY)
  ctx.lineTo(canvasWidth, runningY)
  ctx.strokeStyle = '#e5e7eb'
  ctx.lineWidth = s(1)
  ctx.stroke()

  return runningY - offsetY
}

// The four verdict swatches are dropped for an account without reconciliation: none of those colours
// can appear on its grid, and naming them would report a verdict it never bought.
function drawLegend(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  offsetY: number,
  includeReconciliation: boolean,
) {
  const centerX = canvasWidth / 2
  const dotSize = s(12)
  const gap = s(24)
  const fontSize = s(11)

  ctx.font = `400 ${fontSize}px ${FONT}`

  const verdictItems: { label: string; fill: string; bordered?: boolean }[] = [
    { label: 'Reconciled', fill: COVERAGE_FILL_HEX.reconciled },
    { label: 'Needs review', fill: COVERAGE_FILL_HEX.needs_review },
    { label: 'Unverifiable', fill: COVERAGE_FILL_HEX.unverifiable },
    { label: 'Not checked', fill: COVERAGE_FILL_HEX.not_checked },
    { label: 'Member activity', fill: COVERAGE_FILL_HEX.member_activity },
    {
      label: 'No statement (verified)',
      fill: COVERAGE_FILL_HEX.verified_gap,
      bordered: true,
    },
  ]
  const items: { label: string; fill: string; bordered?: boolean }[] = [
    ...(includeReconciliation
      ? verdictItems
      : [{ label: 'Covered', fill: COVERAGE_FILL_HEX.not_checked }]),
    { label: 'No statement', fill: COVERAGE_FILL_HEX.missing, bordered: true },
  ]

  const widths = items.map(
    (item) => dotSize + s(6) + ctx.measureText(item.label).width,
  )
  const totalW =
    widths.reduce((sum, w) => sum + w, 0) + gap * (items.length - 1)

  let x = centerX - totalW / 2
  const y = offsetY + s(16)

  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'

  items.forEach((item, index) => {
    ctx.beginPath()
    ctx.roundRect(x, y, dotSize, dotSize, s(2))
    ctx.fillStyle = item.fill
    ctx.fill()
    if (item.bordered) {
      ctx.strokeStyle = 'rgba(163, 163, 163, 0.6)'
      ctx.lineWidth = s(1)
      ctx.stroke()
    }

    ctx.fillStyle = '#71717a'
    ctx.fillText(item.label, x + dotSize + s(6), y + dotSize / 2)

    x += widths[index] + gap
  })
}

export function exportCoveragePNG(
  coverage: StatementCoverage,
  options: ExportOptions = {},
): void {
  const { projectName, slug, includeReconciliation = true } = options
  const { accounts } = coverage

  const totalMonths = computeTotalMonths(coverage)
  const chartSvgWidth = MARGIN.left + MARGIN.right + totalMonths * MONTH_WIDTH

  // Ensure canvas is wide enough for both the chart and the summary text
  const minSummaryWidth = 700
  const contentWidth = Math.max(minSummaryWidth, chartSvgWidth)
  const canvasWidth = s(contentWidth)

  // Chart height includes extra space for month labels below
  const monthLabelSpace = 20
  const chartHeight =
    accounts.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom + monthLabelSpace

  const padding = s(40)
  const projectNameH = projectName ? s(28 + 12) : 0
  const titleH = s(20 + 8)
  const headerH = padding + projectNameH + titleH + s(8)
  const chartH = s(chartHeight)
  const legendH = s(44)

  // Measure summary heights using an offscreen canvas
  const measureCanvas = document.createElement('canvas')
  const measureCtx = measureCanvas.getContext('2d')!
  const maxTextWidth = canvasWidth * 0.55
  const summaryH = accounts.reduce(
    (total, account) =>
      total + measureSummaryRowHeight(measureCtx, account, maxTextWidth),
    0,
  )

  const canvasHeight = headerH + chartH + summaryH + legendH + padding

  const canvas = document.createElement('canvas')
  canvas.width = canvasWidth
  canvas.height = canvasHeight
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  // Background
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  // Header
  let currentY = padding

  if (projectName) {
    ctx.fillStyle = '#000000'
    ctx.font = `700 ${s(28)}px ${FONT}`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
    ctx.fillText(projectName, padding, currentY + s(28))
    currentY += projectNameH
  }

  ctx.fillStyle = '#000000'
  ctx.font = `600 ${s(20)}px ${FONT}`
  ctx.textAlign = 'left'
  ctx.fillText('Statement Coverage', padding, currentY + s(20))
  currentY += titleH + s(8)

  // Chart
  drawAccountLabels(ctx, accounts, currentY)
  drawChart(ctx, coverage, currentY)
  currentY += chartH

  // Account summary
  const actualSummaryH = drawAccountSummary(
    ctx,
    accounts,
    canvasWidth,
    currentY,
  )
  currentY += actualSummaryH

  // Legend
  drawLegend(ctx, canvasWidth, currentY, includeReconciliation)

  // Download
  const pngUrl = canvas.toDataURL('image/png')
  const link = document.createElement('a')
  link.href = pngUrl
  const prefix = slug ? `${slug}-` : ''
  link.download = `${prefix}statement-coverage.png`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
